import React, { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { uniqueId } from 'lodash';
import dayjs from 'dayjs';

import InformationalPopover from 'chameleon/ui-stack/ui/popovers/informational-popover';
import WrappedDatePicker from 'chameleon-src/components/assign/AssignManageForm/options/TimeOptions/DateField/WrappedDataPicker';
import DateInput from './DateInput';

const DatePickerField = (props) => {
  const { control } = useFormContext();
  const {
    name,
    title,
    infoPopupText,
    timezone,
    rules,
    minDate = null,
    maxDate = null,
    initialValue,
    error,
    onValueChange = () => {},
    onReset = () => {},
    disabled,
  } = props;

  const inputId = uniqueId(name);
  const popoverRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isChanged, setChanged] = useState(false);

  const renderError = () => (
    <div
      className="pt-4 pb-4 gap-2 text-base text-red-600 h-2"
      data-test-id={`${props['data-test-id']}-error`}
    >
      {error.message}
    </div>
  );

  return (
    <div className="d-block mb-8" data-test-id={props['data-test-id']}>
      <div className="h-8 d-flex flex-row items-center mb-0.8">
        <label
          className="mr-4 text-base text-gray-800 font-medium"
          htmlFor={inputId}
        >
          {title}
        </label>
        <InformationalPopover size="medium" defaultOpen={false}>
          <span
            aria-live="assertive"
            aria-label={infoPopupText}
            className="text-base leading-8"
          >
            {infoPopupText}
          </span>
        </InformationalPopover>
      </div>
      <Controller
        control={control}
        id={inputId}
        name={name}
        rules={rules}
        render={({ field }) => (
          <DateInput
            field={field}
            timezone={timezone}
            popoverRef={popoverRef}
            onResetClick={onReset}
            disabled={disabled}
          >
            <WrappedDatePicker
              onTimeSelect={popoverRef.current?.close}
              timezone={timezone}
              meta={{ error: '', touched: true }}
              input={{
                onChange: (val) => {
                  const formattedVal = val.format('YYYY-MM-DDTHH:mm:ssZ');
                  field.onChange(formattedVal);
                  setChanged(true);
                  setSelectedDate(val);
                  onValueChange(formattedVal);
                },
                value: '',
                name,
              }}
              selectedValue={
                (isChanged &&
                  selectedDate &&
                  selectedDate.format('MM/DD/YYYY hh:mm A')) ||
                initialValue ||
                dayjs()
              }
              minDate={minDate}
              maxDate={maxDate}
              overridingProps={{ class: '' }}
              inline
            />
            <div
              className="datepicker__current-time border-0 border-b border-gray-500 text-14 font-medium"
              role="button"
              tabIndex="0"
              onClick={() => {
                // Just like in our Assign/Update Settings assignment modal, we can Select Current Date and Time
                const now = dayjs().utc(false).tz(timezone);
                field.onChange(now.format('YYYY-MM-DDTHH:mm:ssZ'));
                setChanged(true);
                popoverRef.current?.close();
              }}
            >
              Select Current Date and Time
            </div>
          </DateInput>
        )}
      />
      {error && renderError()}
      {props.children && <div className="mt-4 px-2">{props.children}</div>}
    </div>
  );
};

export default DatePickerField;
